import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wrench, Loader2, Mail, Lock, ArrowRight, ShieldCheck, Boxes, Search } from "lucide-react";
import { useAuth, ApiClientError } from "../lib/auth";

/** SBD is the actual client this platform is built for — a text-based brand chip rather than an
 *  embedded logo image, since no official logo asset file is available to this build. */
function SbdBadge({ className = "" }: { className?: string }) {
  return (
    <span className={`inline-flex items-center rounded-lg bg-brand-yellow px-3 py-1.5 font-display font-bold text-[13px] tracking-tight text-brand-navy shadow-pop ${className}`}>
      STANLEY BLACK &amp; DECKER
    </span>
  );
}

/** The real, official Microsoft four-square mark (a simple, exact geometric mark — unlike a full
 *  logo/wordmark, this is safe to reproduce precisely from its well-known colors). */
function MicrosoftMark() {
  return (
    <span className="inline-grid grid-cols-2 gap-[2px] w-[15px] h-[15px] flex-shrink-0">
      <span className="bg-[#F25022]" />
      <span className="bg-[#7FBA00]" />
      <span className="bg-[#00A4EF]" />
      <span className="bg-[#FFB900]" />
    </span>
  );
}

// This account only exists so the demo SSO button below has something real to authenticate as —
// see the comment on handleSsoBypass for why it still goes through the genuine login endpoint.
const SSO_DEMO_EMAIL = "manager@sbdtcic.demo";
const SSO_DEMO_PASSWORD = "Demo1234!";

const FEATURES = [
  { icon: ShieldCheck, title: "GRC-capped, safety-gated repairs", body: "Guaranteed-repair-cost caps apply automatically at completion, and a repair can't close until all seven safety checks pass." },
  { icon: Boxes, title: "One inventory, every module", body: "Stock updates instantly across POS, repair orders, receiving, and shipping — never a separate count to reconcile." },
  { icon: Search, title: "Real-time order & repair tracking", body: "SBD customers can look up their order or repair status themselves, anytime, with no call to the service center required." },
];

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [ssoBusy, setSsoBusy] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Unable to sign in. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  /**
   * Microsoft Entra ID isn't actually integrated — there's no real SSO handshake here. But every
   * other screen in this app depends on a genuine server-verified session (the JWT+KV session
   * cookie every API call checks), so a purely client-side "pretend you're logged in" would break
   * the moment the dashboard tried to fetch real data. This button gets a real session the honest
   * way: it signs in through the same `login()` call as the form above, using one designated demo
   * account, so "bypass" only means "skip typing the password," not "skip authentication."
   */
  async function onSsoBypass() {
    setError(null);
    setSsoBusy(true);
    try {
      await login(SSO_DEMO_EMAIL, SSO_DEMO_PASSWORD);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : "Unable to sign in. Please try again.");
    } finally {
      setSsoBusy(false);
    }
  }

  return (
    <div className="h-screen overflow-hidden flex bg-card">
      {/* Brand panel — hidden below lg, where the form panel takes the full viewport.
          `overflow-y-auto` is a safety net only: the spacing below is tuned to fit without
          scrolling on any reasonable window height, but if a future addition ever grows this
          panel again, it scrolls internally instead of growing a page-level scrollbar. */}
      <div className="hidden lg:flex lg:w-[45%] xl:w-[42%] relative flex-col justify-between p-10 xl:p-12 overflow-y-auto overflow-x-hidden bg-gradient-to-br from-brand-navy to-brand-navy-deep text-white">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: "radial-gradient(circle, #fff 1px, transparent 1px)", backgroundSize: "22px 22px" }}
          aria-hidden="true"
        />
        <div
          className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-20 blur-3xl"
          style={{ background: "radial-gradient(circle, var(--color-brand-blue), transparent 70%)" }}
          aria-hidden="true"
        />

        <div className="relative mb-6">
          <SbdBadge className="mb-5" />
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
              <Wrench size={18} className="text-brand-yellow" />
            </div>
            <span className="font-display font-bold text-[15px] tracking-tight">Service Center Platform</span>
          </div>
        </div>

        <div className="relative">
          <h1 className="font-display font-bold text-[34px] xl:text-[38px] leading-[1.15] text-balance mb-4">
            One platform for every repair, from intake to invoice.
          </h1>
          <p className="text-slate-300 text-[15px] leading-relaxed max-w-md mb-6">
            Point of sale, repair orders, inventory, and shipping — built for SBD service centers, running end to end on the platform below.
          </p>

          <div className="flex flex-col gap-5">
            {FEATURES.map((f) => (
              <div key={f.title} className="flex gap-3.5">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <f.icon size={16} className="text-brand-yellow" />
                </div>
                <div>
                  <div className="font-semibold text-[13.5px] mb-0.5">{f.title}</div>
                  <div className="text-slate-400 text-[13px] leading-relaxed">{f.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="relative flex items-center justify-between text-slate-500 text-xs mt-6">
          <span>SBD TCIC Replacement</span>
          <span>
            Powered by <span className="font-bold text-slate-300">YASH</span> Technologies
          </span>
        </div>
      </div>

      {/* Form panel — overflow-y-auto is the same safety net as the brand panel above. */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 overflow-y-auto">
        <div className="w-full max-w-[380px]">
          <div className="flex lg:hidden flex-col items-center mb-8">
            <SbdBadge className="mb-4" />
            <div className="w-12 h-12 rounded-xl bg-brand-navy flex items-center justify-center mb-3">
              <Wrench size={22} className="text-brand-yellow" />
            </div>
            <span className="font-display font-bold text-[15px] tracking-tight">Service Center Platform</span>
          </div>

          <h2 className="font-display font-bold text-[26px] mb-1.5">Welcome back</h2>
          <p className="text-ink-muted text-sm mb-8">Sign in to your service center account.</p>

          <form onSubmit={onSubmit}>
            <label className="field-label" htmlFor="email">
              Email
            </label>
            <div className="relative mb-4">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                id="email"
                type="email"
                autoComplete="username"
                required
                className="input pl-9"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@servicecenter.com"
              />
            </div>

            <label className="field-label" htmlFor="password">
              Password
            </label>
            <div className="relative">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                className="input pl-9"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div role="alert" className="text-status-red text-[13px] font-semibold mt-3.5">
                {error}
              </div>
            )}

            <button type="submit" disabled={busy} className="btn-primary w-full justify-center mt-6">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-border" />
            <span className="text-[11px] font-semibold text-ink-muted uppercase tracking-wide">or</span>
            <div className="flex-1 h-px bg-border" />
          </div>

          <button type="button" disabled={ssoBusy} onClick={() => void onSsoBypass()} className="btn-outline w-full justify-center gap-2.5">
            {ssoBusy ? <Loader2 size={16} className="animate-spin" /> : <MicrosoftMark />}
            {ssoBusy ? "Signing in…" : "Continue with Microsoft Entra ID"}
          </button>

          <p className="text-center text-ink-muted text-xs mt-8">
            Looking to track an order or repair?{" "}
            <a href="/portal" className="text-brand-blue font-semibold underline underline-offset-2">
              Visit the customer portal
            </a>
          </p>

          <p className="text-center text-ink-muted/70 text-[11px] mt-6">
            Powered by <span className="font-bold text-ink-muted">YASH</span> Technologies
          </p>
        </div>
      </div>
    </div>
  );
}
